import { isBigQueryDestinationType as isBigQueryType } from "@Simplicity-Education/core";
import {
  Button,
  FormError,
  Input,
  Label,
  Textarea,
  sonnerToast,
  useDocumentTitle,
} from "@simplicity/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { apiFetch } from "../../api";
import type { Destination, Project } from "../../configTypes";
import { DESTRUCTIVE_BUTTON_CLASS, tryParseJson } from "../../utils";
import { TypeBadge } from "../TypeBadge";
import { Breadcrumbs } from "./Breadcrumbs";
import { FormBanner } from "./FormBanner";
import { useDirtyForm } from "./useDirtyForm";
import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";

// `isBigQueryType` is core's `isBigQueryDestinationType` and is deliberately WIDE
// (SIM-2596): this screen renders existing destinations, including archived
// `bigquery` / `bigquery-experimental` rows, which still need their Project ID /
// Dataset ID inputs. Only the create form narrows.

function useDestinationState() {
  const [destination, setDestination] = useState<Destination | null>(null);
  const [projectName, setProjectName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [varsText, setVarsText] = useState("");
  const [secretsText, setSecretsText] = useState("");
  const [varsError, setVarsError] = useState<string | null>(null);
  const [secretsError, setSecretsError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveLabel, setSaveLabel] = useState("Save");
  const [banner, setBanner] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(null);

  const { isDirty, setBaseline, resetBaseline } = useDirtyForm({ name, varsText, secretsText });
  const clearBanner = useCallback(() => setBanner(null), []);

  const flashSaveLabel = useCallback(() => {
    setSaveLabel("\u2713 Saved");
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => setSaveLabel("Save"), 2000);
  }, []);

  return {
    destination,
    setDestination,
    projectName,
    setProjectName,
    loading,
    setLoading,
    error,
    setError,
    name,
    setName,
    varsText,
    setVarsText,
    secretsText,
    setSecretsText,
    varsError,
    setVarsError,
    secretsError,
    setSecretsError,
    saving,
    setSaving,
    saveLabel,
    banner,
    setBanner,
    isDirty,
    setBaseline,
    resetBaseline,
    clearBanner,
    flashSaveLabel,
  };
}

function useLoadDestination(id: string | undefined, s: ReturnType<typeof useDestinationState>) {
  useEffect(() => {
    if (!id) return;
    void apiFetch<Destination>(`/api/destinations/${id}`)
      .then(async (d) => {
        s.setDestination(d);
        s.setName(d.name);
        s.setVarsText(JSON.stringify(d.vars, null, 2));
        s.setSecretsText(JSON.stringify(d.secrets, null, 2));
        s.setBaseline({
          name: d.name,
          varsText: JSON.stringify(d.vars, null, 2),
          secretsText: JSON.stringify(d.secrets, null, 2),
        });
        const proj = await apiFetch<Project>(`/api/projects/${d.projectId}`);
        s.setProjectName(proj.name);
      })
      .catch((e) => s.setError(e.message))
      .finally(() => s.setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch-on-id-change; the closed-over `s` state handle's setters are stable and intentionally not in deps to avoid refetch loops.
  }, [id]);
}

async function handleSave(
  e: React.FormEvent,
  id: string | undefined,
  s: ReturnType<typeof useDestinationState>,
) {
  e.preventDefault();
  const varsResult = tryParseJson(s.varsText);
  const secretsResult = tryParseJson(s.secretsText);
  if (!varsResult.ok) {
    s.setVarsError(varsResult.error);
    return;
  }
  if (!secretsResult.ok) {
    s.setSecretsError(secretsResult.error);
    return;
  }

  s.setSaving(true);
  try {
    const updated = await apiFetch<Destination>(`/api/destinations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        name: s.name.trim(),
        vars: varsResult.value,
        secrets: secretsResult.value,
      }),
    });
    s.setDestination(updated);
    s.resetBaseline();
    s.setBanner({ type: "success", message: "Updated configuration." });
    s.flashSaveLabel();
  } catch (err) {
    s.setBanner({
      type: "error",
      message: err instanceof Error ? err.message : "Save failed",
    });
  } finally {
    s.setSaving(false);
  }
}

async function handleArchive(
  id: string | undefined,
  projectId: string | undefined,
  navigate: ReturnType<typeof useNavigate>,
) {
  if (!confirm("Are you sure you want to archive this destination?")) return;
  try {
    await apiFetch(`/api/destinations/${id}/archive`, { method: "POST" });
    void navigate(`/config/projects/${projectId}`);
  } catch (err) {
    sonnerToast.error(err instanceof Error ? err.message : "Archive failed");
  }
}

function BigQueryVarsForm(props: {
  varsText: string;
  setVarsText: (v: string) => void;
  setVarsError: (v: string | null) => void;
  clearBanner: () => void;
}) {
  const parsed = tryParseJson(props.varsText);
  const vars = parsed.ok ? (parsed.value as Record<string, unknown>) : {};
  const projectId = (vars.projectId as string) ?? "";
  const datasetId = (vars.datasetId as string) ?? "";

  function updateField(key: string, value: string) {
    const next = { ...vars };
    if (value === "") {
      delete next[key];
    } else {
      next[key] = value;
    }
    props.setVarsText(JSON.stringify(next, null, 2));
    props.setVarsError(null);
    props.clearBanner();
  }

  return (
    <div className="mb-4 space-y-4">
      <div>
        <Label htmlFor="dest-project-id" className="form-label">
          Project ID
        </Label>
        <Input
          id="dest-project-id"
          type="text"
          value={projectId}
          onChange={(e) => updateField("projectId", e.target.value)}
          placeholder="GCP project ID"
          className="font-mono"
        />
      </div>
      <div>
        <Label htmlFor="dest-dataset-id" className="form-label">
          Dataset ID
        </Label>
        <Input
          id="dest-dataset-id"
          type="text"
          value={datasetId}
          onChange={(e) => updateField("datasetId", e.target.value)}
          placeholder="Optional — override default dataset"
          className="font-mono"
        />
      </div>
    </div>
  );
}

function GenericVarsField(props: {
  varsText: string;
  setVarsText: (v: string) => void;
  varsError: string | null;
  setVarsError: (v: string | null) => void;
  clearBanner: () => void;
}) {
  return (
    <div className="mb-4">
      <Label htmlFor="dest-vars" className="form-label">
        Variables
      </Label>
      <Textarea
        id="dest-vars"
        className={`min-h-[200px] font-mono ${props.varsError ? "border-destructive" : ""}`}
        value={props.varsText}
        onChange={(e) => {
          props.setVarsText(e.target.value);
          props.setVarsError(null);
          props.clearBanner();
        }}
        onBlur={() => {
          const r = tryParseJson(props.varsText);
          props.setVarsError(r.ok ? null : r.error);
        }}
      />
      {props.varsError && <FormError>{props.varsError}</FormError>}
    </div>
  );
}

function GenericSecretsField(props: {
  secretsText: string;
  setSecretsText: (v: string) => void;
  secretsError: string | null;
  setSecretsError: (v: string | null) => void;
  clearBanner: () => void;
}) {
  return (
    <div className="mb-4">
      <Label htmlFor="dest-secrets" className="form-label">
        Secret References
      </Label>
      <Textarea
        id="dest-secrets"
        className={`min-h-[100px] font-mono ${props.secretsError ? "border-destructive" : ""}`}
        value={props.secretsText}
        onChange={(e) => {
          props.setSecretsText(e.target.value);
          props.setSecretsError(null);
          props.clearBanner();
        }}
        onBlur={() => {
          const r = tryParseJson(props.secretsText);
          props.setSecretsError(r.ok ? null : r.error);
        }}
      />
      {props.secretsError && <FormError>{props.secretsError}</FormError>}
    </div>
  );
}

function DestinationForm({
  id,
  destination,
  s,
  navigate,
}: {
  id: string;
  destination: Destination;
  s: ReturnType<typeof useDestinationState>;
  navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <div className="form-card mb-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg text-foreground">Edit Destination</h2>
        <TypeBadge type={destination.type} />
      </div>
      {s.banner && (
        <FormBanner
          type={s.banner.type}
          message={s.banner.message}
          onDismiss={() => s.setBanner(null)}
        />
      )}
      <form onSubmit={(e) => void handleSave(e, id, s)}>
        <div className="mb-4">
          <Label htmlFor="dest-name" className="form-label">
            Name
          </Label>
          <Input
            id="dest-name"
            type="text"
            value={s.name}
            onChange={(e) => {
              s.setName(e.target.value);
              s.clearBanner();
            }}
            autoComplete="one-time-code"
          />
        </div>

        {isBigQueryType(destination.type) ? (
          <BigQueryVarsForm
            varsText={s.varsText}
            setVarsText={s.setVarsText}
            setVarsError={s.setVarsError}
            clearBanner={() => s.setBanner(null)}
          />
        ) : (
          <GenericVarsField
            varsText={s.varsText}
            setVarsText={s.setVarsText}
            varsError={s.varsError}
            setVarsError={s.setVarsError}
            clearBanner={() => s.setBanner(null)}
          />
        )}

        {isBigQueryType(destination.type) ? (
          <p className="mb-4 text-sm text-muted-foreground">No secrets required.</p>
        ) : (
          <GenericSecretsField
            secretsText={s.secretsText}
            setSecretsText={s.setSecretsText}
            secretsError={s.secretsError}
            setSecretsError={s.setSecretsError}
            clearBanner={() => s.setBanner(null)}
          />
        )}

        <div className="form-actions">
          <Button
            type="button"
            variant="outline"
            className={DESTRUCTIVE_BUTTON_CLASS}
            onClick={() => void handleArchive(id, destination.projectId, navigate)}
          >
            Archive
          </Button>
          <Button type="submit" disabled={s.saving || !s.isDirty || !s.name.trim()}>
            {s.saving ? "Saving..." : s.saveLabel}
          </Button>
        </div>
      </form>
    </div>
  );
}

export function DestinationDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const s = useDestinationState();
  useLoadDestination(id, s);
  const { dialog: unsavedChangesDialog } = useUnsavedChangesGuard(s.isDirty);

  useDocumentTitle(s.destination?.name ?? null, { suffix: " | Simplicity Admin" });

  if (s.loading) return <div className="empty-state">Loading destination...</div>;
  if (s.error || !s.destination || !id) {
    return <div className="empty-state text-destructive">{s.error ?? "Destination not found"}</div>;
  }

  return (
    <div className="mx-auto max-w-4xl">
      <Breadcrumbs
        items={[
          { label: "Projects", to: "/config/projects" },
          {
            label: s.projectName || "...",
            to: `/config/projects/${s.destination.projectId}#destinations`,
          },
          { label: s.destination.name },
        ]}
      />
      <DestinationForm id={id} destination={s.destination} s={s} navigate={navigate} />
      {unsavedChangesDialog}
    </div>
  );
}
